import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Input, Table, Empty, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import './index.scss';
import { MonitoringPaper } from '../MonitoringPaper';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import Spinner from '../../../../components/Spinner';
import { getThumbnailPlaybackByTime } from '../../../../socket/playback';
import IMAGE_QUALITY_PLACEHOLDER_IMAGE from '../../../../assets/images/camera_tampering_placeholder_thumbnail.png';
import { useTranslation } from 'react-i18next';
import PlaybackPlayer from '../../../../components/Playback';

const IMAGE_HEALTH_LOG_QUESTION_ID = 290;

export const formatDurationSmart = (seconds) => {
  const units = [
    { label: 'y', value: 60 * 60 * 24 * 365 },
    { label: 'mo', value: 60 * 60 * 24 * 30 },
    { label: 'w', value: 60 * 60 * 24 * 7 },
    { label: 'd', value: 60 * 60 * 24 },
    { label: 'h', value: 60 * 60 },
    { label: 'm', value: 60 },
    { label: 's', value: 1 },
  ];

  let remaining = seconds;
  const result = [];

  for (const unit of units) {
    const count = Math.floor(remaining / unit.value);
    if (count > 0) {
      result.push(`${count}${unit.label}`);
      remaining %= unit.value;
    }

    if (result.length === 2) break;
  }

  return result.length ? result.join(' ') : '0s';
};

export const ImageHealthLog = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  selectedDate,
}) => {
  const [fullData, setFullData] = useState([]);
  const [dataPageSize, setDataPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { namespace } = useSelector((state) => state);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const [visibleImage, setVisibleImage] = useState(null);
  const [visibleSituation, setVisibleSituation] = useState(null);
  const { t } = useTranslation();

  // FIX 1: Dùng ref để track namespace mới nhất trong thumbnail callbacks
  const namespaceRef = useRef(namespace);
  useEffect(() => {
    namespaceRef.current = namespace;
  }, [namespace]);

  useEffect(() => {
    if (!isActive) {
      setDataPageSize(10);
      setCurrent(1);
      setSearchValue('');
    }
  }, [isActive]);

  const baseFetchData = useMonitoringDataFetch(
    IMAGE_HEALTH_LOG_QUESTION_ID,
    selectedDate,
  );

  // FIX 2: Xóa `current` và `dataPageSize` khỏi dependency array
  // Đây là nguyên nhân chính gây bug: mỗi lần chuyển trang -> current thay đổi
  // -> fetchImageHealthLogData tạo lại -> useMonitoringDataWithInterval re-fetch
  // -> bên trong fetch gọi setCurrent(1) -> reset về trang 1 vô tận
  const fetchImageHealthLogData = useCallback(async () => {
    if (!namespace) return null;
    try {
      setIsLoading(true);
      const rawData = await baseFetchData();

      const transformedData = rawData.map((item) => {
        const durationSeconds = moment(item.endTime).diff(
          moment(item.startTime),
          'seconds',
        );
        return {
          id: item.serial,
          cameraName: item.name,
          serial: item.serial,
          gatewayName: item.nameGateway,
          location: item?.location
            ? item.location === 'undefined'
              ? '_'
              : item.location
            : '_',
          startTimeStr: moment(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
          endTimeStr: moment(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
          startTime: item.startTime,
          endTime: item.endTime,
          action: 'no-action',
          duration: durationSeconds,
          durationStr: formatDurationSmart(durationSeconds),
          thumbnail: null,
          thumbnailLoading: true,
        };
      });

      setFullData(transformedData);
      // FIX 3: Reset về trang 1 chỉ khi fetch xong data mới (đúng chỗ)
      setCurrent(1);
      setIsLoading(false);

      // FIX 4: Xóa slice theo current/dataPageSize.
      // Load thumbnail cho toàn bộ items thay vì chỉ trang hiện tại.
      // Việc dùng current/dataPageSize ở đây khiến: khi deps thay đổi,
      // callback mới được tạo ra nhưng index trong setFullData vẫn dùng
      // index cũ của rawData -> sai dữ liệu + trigger re-fetch loop.
      rawData.forEach((item, index) => {
        if (!item?.serial) {
          setFullData((prev) =>
            prev.map((d, i) =>
              i === index ? { ...d, thumbnailLoading: false } : d,
            ),
          );
          return;
        }

        getThumbnailPlaybackByTime({
          serial: item.serial,
          queryTime: moment(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
          // FIX 5: Dùng namespaceRef.current thay vì namespace từ closure
          // để tránh stale closure khi namespace thay đổi giữa chừng
          namespace: namespaceRef.current,
        })
          .then((res) => {
            console.log('🚀 ~ ImageHealthLog ~ res:', res);
            if (!isMountedRef.current) return;
            setFullData((prev) =>
              prev.map((d, i) =>
                i === index
                  ? {
                      ...d,
                      thumbnail: res?.imageBase64 ?? null,
                      thumbnailLoading: false,
                    }
                  : d,
              ),
            );
          })
          .catch((err) => {
            beeXLogger.debug('getPlaybackThumbnail - err: ', err);
            if (!isMountedRef.current) return;
            setFullData((prev) =>
              prev.map((d, i) =>
                i === index ? { ...d, thumbnailLoading: false } : d,
              ),
            );
          });
      });

      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
    // FIX 6: Dependency array chỉ còn baseFetchData và namespace
    // Không có current, dataPageSize -> không re-fetch khi chuyển trang
  }, [baseFetchData, namespace]);

  const { reload } = useMonitoringDataWithInterval(
    fetchImageHealthLogData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  useEffect(() => {
    if (isActive && isLoading && !loadingTimeout) {
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) {
          setLoadingTimeout(true);
        }
      }, 30000);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isActive, isLoading, loadingTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  const getActionLabel = (action) => {
    const actionMap = {
      're-calibrate': 'RE-CALIBRATE',
      'clear-alert': 'CLEAR ALERT',
      'no-action': 'NO ACTION',
    };
    return actionMap[action] || action?.toUpperCase();
  };

  const columns = [
    {
      title: 'IMAGE',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      align: 'center',
      render: (_, record) => {
        if (record.thumbnailLoading) {
          return (
            <div
              style={{
                cursor: 'pointer',
                width: 40,
                height: 40,
                background: '#f0f0f0',
                borderRadius: 4,
                margin: '0 auto',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
              onClick={() => setVisibleImage(record)}
            />
          );
        }
        if (!record.thumbnail) {
          return (
            <img
              src={IMAGE_QUALITY_PLACEHOLDER_IMAGE}
              alt="Thumbnail"
              width={40}
              height={40}
              style={{
                cursor: 'pointer',
                borderRadius: 4,
                objectFit: 'cover',
                minWidth: 40,
                minHeight: 40,
              }}
              onClick={() => setVisibleImage(record)}
            />
          );
        }
        return (
          <img
            src={record.thumbnail}
            alt="Thumbnail"
            width={40}
            height={40}
            style={{
              cursor: 'pointer',
              borderRadius: 4,
              objectFit: 'cover',
              minWidth: 40,
              minHeight: 40,
            }}
            onClick={() => setVisibleImage(record)}
          />
        );
      },
    },
    {
      title: 'CAMERA',
      dataIndex: 'cameraName',
      key: 'cameraName',
      align: 'center',
      render: (text, record) => (
        <div className="camera-cell">
          <div className="camera-name">{text}</div>
        </div>
      ),
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      align: 'center',
      width: 150,
      render: (text) => (
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 150,
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'EVENT',
      dataIndex: 'event',
      key: 'event',
      align: 'center',
      render: () => (
        <span style={{ color: '#faad14', fontWeight: 'bold' }}>Tampering</span>
      ),
    },
    {
      title: 'DETECTED',
      dataIndex: 'startTimeStr',
      key: 'startTimeStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'DURATION',
      dataIndex: 'durationStr',
      key: 'durationStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (text) => (
        <span style={{ color: '#0e9347', fontWeight: 'bold' }}>Resolve</span>
      ),
    },
    {
      title: 'RESOLVED AT',
      dataIndex: 'endTimeStr',
      key: 'endTimeStr',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'ACTIONS',
      dataIndex: 'action',
      key: 'action',
      width: '18%',
      align: 'center',
      render: (key, record) => (
        <span
          className="btn-view-situation"
          onClick={() => setVisibleSituation(record)}
        >
          {t('View situation')}
        </span>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    if (!searchValue.trim()) {
      return fullData;
    }
    return fullData.filter(
      (item) =>
        item.cameraName.toLowerCase().includes(searchValue.toLowerCase()) ||
        item.serial.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [fullData, searchValue]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort(
      (a, b) => moment(b.startTime).valueOf() - moment(a.startTime).valueOf(),
    );
  }, [filteredData]);

  // FIX 7: onPageChange và onShowSizeChange chỉ update state local,
  // KHÔNG trigger fetch lại nữa vì current/dataPageSize đã bị xóa khỏi deps
  const onPageChange = (page) => {
    setCurrent(page);
  };

  const onShowSizeChange = (current, pageSize) => {
    setDataPageSize(pageSize);
    setCurrent(1);
  };

  return (
    <MonitoringPaper>
      <div className="image-health-log">
        {isLoading ? (
          <>
            <Spinner />
            {loadingTimeout && <Empty description="No data" />}
          </>
        ) : (
          <>
            <div className="log-header">
              <h2 className="log-title">Image Health Log</h2>
              <Input
                placeholder="Search by camera name or serial..."
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setCurrent(1);
                }}
                allowClear
                className="search-input"
                width={300}
              />
            </div>

            <Table
              columns={columns}
              dataSource={sortedData.map((item, index) => ({
                ...item,
                key: `${item.id}-${index}`,
              }))}
              pagination={{
                onChange: onPageChange,
                onShowSizeChange,
                pageSize: dataPageSize,
                current,
                total: sortedData.length,
                position: ['bottomRight'],
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]} - ${range[1]} of ${total}`,
              }}
              className="health-table"
              bordered={false}
              size="small"
            />
          </>
        )}
      </div>

      <Modal
        visible={!!visibleImage}
        title={`TAMPERING - ${visibleImage?.cameraName} - ${visibleImage?.startTimeStr}`}
        onCancel={() => setVisibleImage(null)}
        footer={null}
      >
        <img
          src={visibleImage?.thumbnail || IMAGE_QUALITY_PLACEHOLDER_IMAGE}
          alt="Thumbnail"
          style={{
            objectFit: 'cover',
            width: '100%',
            height: 'auto',
          }}
        />
      </Modal>

      <Modal
        visible={!!visibleSituation}
        title={`TAMPERING - ${visibleSituation?.cameraName} - ${visibleSituation?.startTimeStr}`}
        onCancel={() => setVisibleSituation(null)}
        footer={null}
      >
        {visibleSituation?.serial && (
          <PlaybackPlayer
            key={visibleSituation?.serial + visibleSituation?.startTime}
            queryTime={visibleSituation?.startTime}
            thumbnail={visibleSituation?.thumbnail}
            serial={visibleSituation?.serial}
            allowFullScreen={true}
            autoPlay={true}
            duration={visibleSituation?.duration}
            variantPage="dashboard"
          />
        )}
      </Modal>
    </MonitoringPaper>
  );
};
